import { useCallback, useState } from 'react';

/**
 * How wide the data rail is, remembered.
 *
 * A six-column result set wants more room than a paragraph does, and only the
 * reader knows which they are looking at — so the width is theirs to set, and it
 * survives the next session rather than snapping back to a default they have
 * already rejected.
 *
 * Clamped on the way in AND on the way out: a stored value from a wider window,
 * or a hand-edited one, must not leave the reading column with nothing.
 */
const KEY = 'discover.dataRailWidth';
export const RAIL_MIN = 320;
export const RAIL_MAX = 900;
export const RAIL_DEFAULT = 420;

export const clampRailWidth = (value) => {
  // `null` and `''` mean "nothing stored", not "zero pixels": `Number(null)` is
  // 0, which would silently clamp an absent value to the narrowest rail rather
  // than the default.
  if (value === null || value === undefined || value === '') return RAIL_DEFAULT;
  const width = Number(value);
  if (!Number.isFinite(width)) return RAIL_DEFAULT;
  return Math.min(RAIL_MAX, Math.max(RAIL_MIN, Math.round(width)));
};

export const useRailWidth = () => {
  const [width, setWidth] = useState(() => {
    try {
      const stored = window.localStorage.getItem(KEY);
      return stored == null ? RAIL_DEFAULT : clampRailWidth(stored);
    } catch {
      // Private browsing, or storage denied. A default the reader can drag is a
      // working rail; refusing to render one is not.
      return RAIL_DEFAULT;
    }
  });

  const set = useCallback((next) => {
    const clamped = clampRailWidth(next);
    setWidth(clamped);
    try {
      window.localStorage.setItem(KEY, String(clamped));
    } catch {
      // Same again: the drag still works for this session.
    }
  }, []);

  return [width, set];
};
